// Master-admin tenancy panel — per-company module flags + billing-
// transfer queue + subscription activation. Visible to is_master_admin
// only. Smeding and hidden tenants are excluded by default.
import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Building2, ArrowRightLeft, Power, ShieldCheck, Loader2, Check, X, Trash2, AlertTriangle, RotateCcw } from 'lucide-react';
import { api } from '@/pages/planning/_helpers';

export default function MasterAdminTenantsPage() {
  const { isMasterAdmin } = useAuth();
  const [tab, setTab] = useState('tenants');
  const [tenants, setTenants] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [audit, setAudit] = useState([]);
  const [mode, setMode] = useState('unknown');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isMasterAdmin) return;
    setLoading(true);
    try {
      const [t, b, m, d, a] = await Promise.all([
        api('/admin/tenants'),
        api('/admin/billing-transfers'),
        api('/payments/mode').catch(() => ({ mode: 'unknown' })),
        api('/admin/deleted-tenants').catch(() => []),
        api('/admin/deletion-audit-log').catch(() => []),
      ]);
      setTenants(t || []); setTransfers(b || []); setMode(m?.mode || 'unknown'); setDeleted(d || []); setAudit(a || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [isMasterAdmin]);
  useEffect(() => { load(); }, [load]);

  if (!isMasterAdmin) return <Navigate to="/dashboard" replace />;

  const pending = transfers.filter((t) => t.status === 'pending');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="master-tenants-page">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl tracking-tight">Tenant administration</h1>
            <p className="text-sm text-muted-foreground mt-1">Modules, subscriptions and billing-transfer queue for every non-Smeding tenant.</p>
          </div>
          <Badge
            variant={mode === 'live' ? 'default' : mode === 'test' ? 'secondary' : 'outline'}
            className={
              mode === 'live'
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white tracking-wider uppercase'
                : mode === 'test'
                  ? 'bg-amber-500/20 text-amber-700 border-amber-500/40 tracking-wider uppercase'
                  : 'tracking-wider uppercase'
            }
            data-testid="stripe-mode-badge"
          >
            Stripe · {mode}
          </Badge>
        </header>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="tenants" data-testid="mt-tab-tenants"><Building2 className="h-4 w-4 mr-2" />Tenants ({tenants.length})</TabsTrigger>
            <TabsTrigger value="transfers" data-testid="mt-tab-transfers"><ArrowRightLeft className="h-4 w-4 mr-2" />Billing transfers{pending.length > 0 && <Badge variant="destructive" className="ml-2 px-1.5 py-0 h-4 text-[10px]">{pending.length}</Badge>}</TabsTrigger>
            <TabsTrigger value="deleted" data-testid="mt-tab-deleted"><Trash2 className="h-4 w-4 mr-2" />Recently deleted ({deleted.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="tenants" className="mt-6 space-y-4">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : tenants.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground" data-testid="mt-tenants-empty">No tenants yet. New companies signed up via /signup will appear here.</CardContent></Card>
            ) : (
              tenants.map((tn) => <TenantCard key={tn.id} tenant={tn} onChanged={load} />)
            )}
          </TabsContent>

          <TabsContent value="transfers" className="mt-6 space-y-3">
            {transfers.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground" data-testid="mt-transfers-empty">No billing-transfer requests.</CardContent></Card>
            ) : transfers.map((t) => <TransferCard key={t.id} req={t} onChanged={load} />)}
          </TabsContent>

          <TabsContent value="deleted" className="mt-6 space-y-3">
            <p className="text-sm text-muted-foreground">Deleted tenants stay fully locked but recoverable for 30 days. After that they are purged automatically and permanently.</p>
            {deleted.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground" data-testid="mt-deleted-empty">No deleted tenants. Nothing pending purge.</CardContent></Card>
            ) : deleted.map((tn) => <DeletedTenantCard key={tn.name} tenant={tn} onChanged={load} />)}

            <Card data-testid="mt-audit-card">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Deletion records</CardTitle></CardHeader>
              <CardContent>
                {audit.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="mt-audit-empty">No deletions recorded yet.</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {audit.map((a, i) => (
                      <li key={i} className="flex flex-wrap items-center gap-2 border-b last:border-0 pb-2" data-testid={`mt-audit-row-${i}`}>
                        <span className="text-xs text-muted-foreground w-36">{a.created_at?.slice(0, 16).replace('T', ' ')}</span>
                        <Badge variant="outline" className="text-[10px]">{a.event}</Badge>
                        <span className="flex-1 min-w-[200px]">{a.subject}</span>
                        <Badge variant={a.email_sent ? 'default' : 'secondary'} className="text-[10px]">{a.email_sent ? 'emailed' : 'logged only'}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}


function TenantCard({ tenant, onChanged }) {
  const [modules, setModules] = useState(tenant.modules || []);
  const [available, setAvailable] = useState([]);
  const [saving, setSaving] = useState(false);
  const [platformModules, setPlatformModules] = useState(tenant.platform_modules || null);
  const [catalog, setCatalog] = useState([]);
  const [availPlatform, setAvailPlatform] = useState([]);
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [priceCents, setPriceCents] = useState(tenant.subscription_amount_cents || '');
  const [currency, setCurrency] = useState((tenant.subscription_currency || 'eur').toLowerCase());
  const [planName, setPlanName] = useState(tenant.subscription_plan_name || 'Standard');
  const [savingPrice, setSavingPrice] = useState(false);
  const [delText, setDelText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const removeTenant = async () => {
    if (delText.trim() !== tenant.name) { toast.error('Type the exact company name to confirm'); return; }
    setDeleting(true);
    try {
      const r = await api(`/admin/tenants/${encodeURIComponent(tenant.name)}`, { method: 'DELETE' });
      toast.success(`${tenant.name} deleted — ${r.users_locked} login(s) locked · recoverable for ${r.days_left} days`);
      if (!r.email_sent) toast.warning('Confirmation email not delivered (Resend sender domain not verified) — the deletion is recorded under "Recently deleted → Deletion records".');
      await onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setDeleting(false); }
  };

  useEffect(() => {
    api(`/admin/tenants/${encodeURIComponent(tenant.name)}`).then((d) => {
      setAvailable(d.available_modules || []);
      setCatalog(d.platform_catalog || []);
      const ap = d.available_platform_modules || [];
      setAvailPlatform(ap);
      // No explicit list stored => tenant has ALL platform modules.
      setPlatformModules((prev) => (prev != null ? prev : (d.company?.platform_modules ?? ap)));
    }).catch(() => {});
  }, [tenant.name]);

  const toggle = (m) => setModules((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]);
  const togglePlatform = (m) => setPlatformModules((prev) => {
    const cur = prev || [];
    return cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m];
  });
  const toggleGroup = (group) => setPlatformModules((prev) => {
    const cur = prev || [];
    const keys = group.features.map((f) => f.key);
    const allOn = keys.every((k) => cur.includes(k));
    return allOn ? cur.filter((k) => !keys.includes(k)) : [...new Set([...cur, ...keys])];
  });

  const save = async () => {
    setSaving(true);
    try { await api(`/admin/tenants/${encodeURIComponent(tenant.name)}/modules`, { method: 'PUT', body: JSON.stringify({ modules }) }); toast.success('Modules updated'); await onChanged(); }
    catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const savePlatform = async () => {
    setSavingPlatform(true);
    try {
      await api(`/admin/tenants/${encodeURIComponent(tenant.name)}/platform-modules`, {
        method: 'PUT', body: JSON.stringify({ platform_modules: platformModules || [] }),
      });
      toast.success('Platform access updated — users see this on next refresh');
      await onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setSavingPlatform(false); }
  };

  const savePrice = async () => {
    setSavingPrice(true);
    try {
      await api(`/admin/tenants/${encodeURIComponent(tenant.name)}/pricing`, {
        method: 'PUT',
        body: JSON.stringify({ amount_cents: parseInt(priceCents, 10), currency, plan_name: planName }),
      });
      toast.success('Pricing saved — tenant can now self-checkout');
      await onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setSavingPrice(false); }
  };

  const activate = async () => {
    try { await api(`/admin/tenants/${encodeURIComponent(tenant.name)}/activate`, { method: 'POST', body: JSON.stringify({ plan_name: planName || 'Standard' }) }); toast.success(`${tenant.name} activated`); await onChanged(); }
    catch (e) { toast.error(e.message); }
  };

  const sub = tenant.subscription;
  return (
    <Card data-testid={`mt-tenant-${tenant.id}`}>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            {tenant.name}
            {tenant.is_hidden && <Badge variant="outline" className="text-xs">hidden</Badge>}
            <Badge variant={sub?.status === 'active' ? 'default' : sub?.status === 'expired' ? 'destructive' : 'secondary'}>{sub?.status || '—'}</Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            {tenant.user_count} user{tenant.user_count === 1 ? '' : 's'} · {tenant.admin_count} admin{tenant.admin_count === 1 ? '' : 's'}
            {sub?.trial_ends_at && <> · trial ends {sub.trial_ends_at.slice(0, 10)}</>}
          </p>
        </div>
        {sub?.status !== 'active' && (
          <Button size="sm" variant="outline" onClick={activate} data-testid={`mt-tenant-activate-${tenant.id}`}>
            <Power className="h-4 w-4 mr-2" />Activate manually
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Subscription pricing (drives the tenant's Stripe Checkout) */}
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Subscription pricing</p>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="text-xs text-muted-foreground">Amount (cents)</label>
              <Input type="number" min="100" step="100" value={priceCents} onChange={(e) => setPriceCents(e.target.value)} className="w-32" data-testid={`mt-price-cents-${tenant.id}`} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Currency</label>
              <Input value={currency} onChange={(e) => setCurrency(e.target.value.toLowerCase())} maxLength={3} className="w-20" data-testid={`mt-price-currency-${tenant.id}`} />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-muted-foreground">Plan name</label>
              <Input value={planName} onChange={(e) => setPlanName(e.target.value)} data-testid={`mt-price-plan-${tenant.id}`} />
            </div>
            <Button size="sm" onClick={savePrice} disabled={savingPrice || !priceCents} data-testid={`mt-price-save-${tenant.id}`}>
              {savingPrice && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save price
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Once a price is saved, the tenant's company-admin page shows a "Pay &amp; activate" button.
            On successful Stripe payment, the subscription flips to active automatically.
          </p>
        </div>

        {/* Platform access — two-level sellable catalog. Gates the
            sidebar groups + routes for every user in this tenant. */}
        <div data-testid={`mt-platform-section-${tenant.id}`}>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Platform access</p>
          <p className="text-xs text-muted-foreground mb-3">Grant any mix of modules. Turning one off hides it from the sidebar and blocks its pages for this tenant. Tick a group to grant all of it, or pick individual sub-modules.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 mb-3">
            {catalog.map((group) => {
              const cur = platformModules || [];
              const keys = group.features.map((f) => f.key);
              const allOn = keys.every((k) => cur.includes(k));
              const isStandalone = group.standalone || (group.features.length === 1 && group.features[0].key === group.key);
              if (isStandalone) {
                const f = group.features[0];
                return (
                  <label key={group.key} className="inline-flex items-center gap-2 text-sm cursor-pointer font-medium">
                    <Checkbox checked={cur.includes(f.key)} onCheckedChange={() => togglePlatform(f.key)} data-testid={`mt-pmod-${tenant.id}-${f.key}`} />
                    <span>{group.label}</span>
                  </label>
                );
              }
              return (
                <div key={group.key} className="space-y-1.5">
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer font-semibold">
                    <Checkbox checked={allOn} onCheckedChange={() => toggleGroup(group)} data-testid={`mt-pgroup-${tenant.id}-${group.key}`} />
                    <span>{group.label}</span>
                  </label>
                  <div className="pl-6 space-y-1">
                    {group.features.map((f) => (
                      <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer text-muted-foreground">
                        <Checkbox checked={cur.includes(f.key)} onCheckedChange={() => togglePlatform(f.key)} data-testid={`mt-pmod-${tenant.id}-${f.key}`} />
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-right">
            <Button size="sm" onClick={savePlatform} disabled={savingPlatform} data-testid={`mt-platform-save-${tenant.id}`}>{savingPlatform && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save platform access</Button>
          </div>
        </div>

        {/* Module access */}
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Module access</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-3">
            {(available.length ? available : modules).map((m) => (
              <label key={m} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={modules.includes(m)} onCheckedChange={() => toggle(m)} data-testid={`mt-mod-${tenant.id}-${m}`} />
                <span>{m}</span>
              </label>
            ))}
          </div>
          <div className="text-right">
            <Button size="sm" onClick={save} disabled={saving} data-testid={`mt-tenant-save-${tenant.id}`}>{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save modules</Button>
          </div>
        </div>

        {/* Danger zone — irreversible full-tenant delete (abandoned trials). */}
        <div className="rounded-lg border border-rose-300 bg-rose-50/60 p-4" data-testid={`mt-tenant-danger-${tenant.id}`}>
          <p className="text-xs uppercase tracking-wider text-rose-700 font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Danger zone</p>
          <p className="text-xs text-rose-800/80 mb-2">Deletes this tenant: every login is locked immediately and the company disappears from the active list. All data (workers, shifts, picking, handovers) stays <strong>recoverable for 30 days</strong> from the “Recently deleted” tab, then it is purged permanently. Type <strong>{tenant.name}</strong> to confirm.</p>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={delText} onChange={(e) => setDelText(e.target.value)} placeholder={tenant.name} className="max-w-xs bg-white" data-testid={`mt-tenant-del-input-${tenant.id}`} />
            <Button size="sm" variant="destructive" onClick={removeTenant} disabled={deleting || delText.trim() !== tenant.name} data-testid={`mt-tenant-del-btn-${tenant.id}`}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}Delete tenant
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function DeletedTenantCard({ tenant, onChanged }) {
  const [busy, setBusy] = useState('');
  const [purgeText, setPurgeText] = useState('');

  const restore = async () => {
    setBusy('restore');
    try {
      const r = await api(`/admin/deleted-tenants/${encodeURIComponent(tenant.name)}/restore`, { method: 'POST' });
      toast.success(`${tenant.name} restored — ${r.users_restored} login(s) active again`);
      await onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(''); }
  };

  const purgeNow = async () => {
    setBusy('purge');
    try {
      await api(`/admin/deleted-tenants/${encodeURIComponent(tenant.name)}`, { method: 'DELETE' });
      toast.success(`${tenant.name} purged permanently`);
      await onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(''); }
  };

  return (
    <Card className="border-rose-200" data-testid={`mt-deleted-${tenant.name}`}>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            {tenant.name}
            <Badge variant="destructive">deleted</Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Deleted {tenant.deleted_at?.slice(0, 10)}{tenant.deleted_by ? ` by ${tenant.deleted_by}` : ''} · {tenant.user_count} locked login{tenant.user_count === 1 ? '' : 's'} ·
            {' '}<span className="text-rose-700 font-medium">purges in {tenant.days_left} day{tenant.days_left === 1 ? '' : 's'}</span>
          </p>
        </div>
        <Button size="sm" onClick={restore} disabled={!!busy} data-testid={`mt-deleted-restore-${tenant.name}`}>
          {busy === 'restore' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />}Restore tenant
        </Button>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-rose-300 bg-rose-50/60 p-3">
          <p className="text-xs text-rose-800/80 mb-2 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" />Purge now — wipes everything immediately, no recovery. Type <strong>{tenant.name}</strong> to confirm.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={purgeText} onChange={(e) => setPurgeText(e.target.value)} placeholder={tenant.name} className="max-w-xs bg-white" data-testid={`mt-deleted-purge-input-${tenant.name}`} />
            <Button size="sm" variant="destructive" onClick={purgeNow} disabled={!!busy || purgeText.trim() !== tenant.name} data-testid={`mt-deleted-purge-btn-${tenant.name}`}>
              {busy === 'purge' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}Purge permanently
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function TransferCard({ req, onChanged }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    setBusy(true);
    try { await api(`/admin/billing-transfers/${req.id}/${kind}`, { method: 'POST', body: JSON.stringify({ notes }) }); toast.success(`Transfer ${kind}d`); await onChanged(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Card data-testid={`mt-transfer-${req.id}`}>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base">{req.company} — {req.new_payer_name}</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">Requested by {req.requested_by_name} ({req.requested_by_email}) · {req.created_at?.slice(0, 10)}</p>
        </div>
        <Badge variant={req.status === 'approved' ? 'default' : req.status === 'rejected' ? 'destructive' : 'secondary'}>{req.status}</Badge>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <div><span className="text-muted-foreground">New email: </span>{req.new_payer_email}</div>
          <div><span className="text-muted-foreground">Existing user: </span>{req.new_payer_user_id || '—'}</div>
          <div><span className="text-muted-foreground">Legal name: </span>{req.company_legal_name || '—'}</div>
          <div><span className="text-muted-foreground">IBAN: </span>{req.iban || '—'}</div>
          <div><span className="text-muted-foreground">VAT: </span>{req.vat_number || '—'}</div>
          <div><span className="text-muted-foreground">Address: </span>{req.billing_address || '—'}</div>
        </div>
        {req.reason && <p className="text-xs text-muted-foreground italic">Reason: {req.reason}</p>}
        {req.status === 'pending' && (
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Decision notes (optional)" className="max-w-md" data-testid={`mt-transfer-notes-${req.id}`} />
            <Button size="sm" onClick={() => act('approve')} disabled={busy} data-testid={`mt-transfer-approve-${req.id}`}><Check className="h-4 w-4 mr-1" />Approve</Button>
            <Button size="sm" variant="outline" onClick={() => act('reject')} disabled={busy} data-testid={`mt-transfer-reject-${req.id}`}><X className="h-4 w-4 mr-1" />Reject</Button>
          </div>
        )}
        {req.status !== 'pending' && req.decision_notes && (
          <p className="text-xs text-muted-foreground">Decision: {req.decision_notes}</p>
        )}
      </CardContent>
    </Card>
  );
}
